/**
 * BackButton — direction-aware navigation control.
 *
 * Rule (per product spec 2026-02-27, updated iter270):
 *   • English / LTR → arrow on the LEFT,  chevron points LEFT
 *   • Arabic  / RTL → arrow on the RIGHT, chevron points RIGHT
 *
 * The component itself is just an inline-flex button — the LEFT/RIGHT
 * placement inside a header is achieved by the parent header layout
 * automatically because we render this as the FIRST child and the
 * surrounding flex container reverses in RTL via `dir="rtl"` on <html>.
 *
 * The icon flip is the part this component owns: it picks ChevronRight
 * when the active language is right-to-left, ChevronLeft otherwise.
 * Chevrons are the spec'd icon for "back"; we used to use Arrow* which
 * was visually heavier.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { hasInAppHistory } from '../hooks/useSmartBack';

export default function BackButton({
  to,
  onClick,
  className = 'admin-back-btn',
  size = 20,
  label,
  testId = 'back-btn',
  ...rest
}) {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isRTL =
    lang === 'ar' ||
    (typeof document !== 'undefined' && document.documentElement?.dir === 'rtl');

  const Icon = isRTL ? ChevronRight : ChevronLeft;

  const handleClick = (e) => {
    if (onClick) return onClick(e);
    if (to) return navigate(to);
    if (hasInAppHistory()) return navigate(-1);
    return navigate('/', { replace: true });
  };

  const ariaLabel = label || (isRTL ? 'رجوع' : 'Back');

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={testId}
      data-rtl={isRTL ? 'true' : 'false'}
      {...rest}
    >
      <Icon size={size} aria-hidden="true" />
      {label && <span className="back-btn-label">{label}</span>}
    </button>
  );
}
